import React, { useState, useEffect } from 'react';
import { getTodayDateString } from '../utils/dateUtils';
import {
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalFooter, ModalBody, ModalCloseButton,
    Button, FormControl, FormLabel, Input, Select, Textarea, useToast, Box
} from '@chakra-ui/react';
import AsyncSelect from 'react-select/async';
import { authAPI } from '../services/api';

const loadStudents = (input) =>
    authAPI.getStudents({ search: input, limit: 20 })
        .then(res => res.data.map(s => ({
            value: s.id,
            label: `${s.full_name} (${s.class?.name || '未分班'})`,
            student: s
        })));

const LeaveApplicationModal = ({ isOpen, onClose, onSuccess, defaultDate }) => {
    const [selectedStudentOption, setSelectedStudentOption] = useState(null);
    const [formData, setFormData] = useState({
        date: defaultDate || getTodayDateString(),
        endDate: defaultDate || getTodayDateString(),
        type: 'PERSONAL',
        reason: '',
        targetClassId: ''
    });

    useEffect(() => {
        if (isOpen) {
            const d = defaultDate || getTodayDateString();
            setFormData(prev => ({ ...prev, date: d, endDate: d }));
        }
    }, [isOpen, defaultDate]);
    const [isLoading, setIsLoading] = useState(false);
    const toast = useToast();

    // Derived: Selected Student's Classes
    const selectedStudent = selectedStudentOption?.student || null;
    const studentClasses = selectedStudent ? (selectedStudent.classes || []) : [];
    // Fallback V1
    if (selectedStudent && selectedStudent.class && !studentClasses.find(c => c.id === selectedStudent.class.id)) {
        studentClasses.push(selectedStudent.class);
    }

    const handleSubmit = async () => {
        const matchedStudent = selectedStudentOption?.student || null;

        if (!matchedStudent) {
            toast({ title: "請選擇學生", status: "warning" });
            return;
        }

        if (!formData.date || !formData.endDate) {
            toast({ title: "請選擇完整的請假日期範圍", status: "warning" });
            return;
        }

        const start = new Date(formData.date);
        const end = new Date(formData.endDate);

        if (end < start) {
            toast({ title: "結束日期不能早於開始日期", status: "warning" });
            return;
        }

        setIsLoading(true);
        try {
            // Smart Logic: Calculate Valid Dates
            const calcRes = await authAPI.leaves.calculateDates({
                student_id: matchedStudent.id,
                start_date: formData.date,
                end_date: formData.endDate,
                class_id: formData.targetClassId || null
            });

            const validDates = calcRes.data.valid_dates;
            const scheduleInfo = calcRes.data.schedule_info;

            if (validDates.length === 0) {
                toast({ title: "所選日期區間無課程/無需請假", status: "info" });
                setIsLoading(false); // Fix: Reset loading
                return;
            }

            // Confirmation Logic
            const confirmMsg = `
系統依據課表 (${scheduleInfo}) 檢測與計算：
您選取的期間內共有 ${validDates.length} 個上課日需請假：
${validDates.join(', ')}

確定要進行登記嗎？`.trim();

            if (!window.confirm(confirmMsg)) {
                setIsLoading(false);
                return;
            }

            // Submit all leaves in parallel
            const responses = await Promise.all(validDates.map(dateStr =>
                authAPI.leaves.createLeave({
                    student_id: matchedStudent.id,
                    date: dateStr,
                    type: formData.type,
                    reason: formData.reason,
                    class_id: formData.targetClassId ? Number(formData.targetClassId) : null // 僅請特定課程時帶；空=整天請假
                })
            ));

            toast({ title: `請假登記成功 (共 ${validDates.length} 天)`, status: "success" });

            // Extract created items for Optimistic UI
            const newLeaves = responses.map(res => res.data);

            // Reset form
            setSelectedStudentOption(null);
            setFormData(prev => ({
                ...prev,
                reason: '',
                date: getTodayDateString(),
                endDate: getTodayDateString(),
                targetClassId: ''
            }));

            if (onSuccess) onSuccess(newLeaves);
            onClose();

        } catch (error) {
            console.error(error);
            toast({ title: "登記失敗", description: error.response?.data?.detail || "部分或全部日期登記失敗", status: "error" });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} isCentered size="lg">
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>登記請假</ModalHeader>
                <ModalCloseButton />
                <ModalBody spacing={4} as={Box}>
                    <FormControl mb={4} isRequired>
                        <FormLabel>選擇學生</FormLabel>
                        <AsyncSelect
                            loadOptions={loadStudents}
                            value={selectedStudentOption}
                            onChange={(opt) => { setSelectedStudentOption(opt); setFormData(prev => ({ ...prev, targetClassId: '' })); }}
                            placeholder="輸入姓名搜尋..."
                            isClearable
                            noOptionsMessage={() => '輸入至少 1 個字開始搜尋'}
                            loadingMessage={() => '搜尋中...'}
                            menuPortalTarget={document.body}
                            styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                        />
                    </FormControl>

                    {/* Class Filter */}
                    <FormControl mb={4}>
                        <FormLabel>請假班級 (選修)</FormLabel>
                        <Select
                            value={formData.targetClassId}
                            onChange={e => setFormData({ ...formData, targetClassId: e.target.value })}
                            isDisabled={!selectedStudent}
                            placeholder={!selectedStudent ? "請先選擇學生" : undefined}
                        >
                            <option value="">全部班級 (預設)</option>
                            {studentClasses.map(cls => (
                                <option key={cls.id} value={cls.id}>
                                    僅 {cls.name} ({cls.department === 'English' ? '英文' : '安親'})
                                </option>
                            ))}
                        </Select>
                        <p style={{ fontSize: '0.8em', color: 'gray', marginTop: '4px' }}>
                            若只請特定課程 (如英文班)，請在此選擇，系統將自動過濾非上課日。
                        </p>
                    </FormControl>

                    <Box display="flex" gap={4} mb={4}>
                        <FormControl flex={1}>
                            <FormLabel>開始日期</FormLabel>
                            <Input
                                type="date"
                                value={formData.date}
                                onChange={e => {
                                    const newDate = e.target.value;
                                    setFormData(prev => ({
                                        ...prev,
                                        date: newDate,
                                        // Auto-update End Date if it was same as Start Date (convenience)
                                        endDate: prev.endDate < newDate ? newDate : prev.endDate
                                    }));
                                }}
                            />
                        </FormControl>
                        <FormControl flex={1}>
                            <FormLabel>結束日期</FormLabel>
                            <Input
                                type="date"
                                value={formData.endDate}
                                onChange={e => setFormData({ ...formData, endDate: e.target.value })}
                            />
                        </FormControl>
                    </Box>
                    <FormControl mb={4}>
                        <FormLabel>假別</FormLabel>
                        <Select
                            value={formData.type}
                            onChange={e => setFormData({ ...formData, type: e.target.value })}
                        >
                            <option value="PERSONAL">事假</option>
                            <option value="SICK">病假</option>
                            <option value="OTHER">其他</option>
                        </Select>
                    </FormControl>
                    <FormControl mb={4}>
                        <FormLabel>備註</FormLabel>
                        <Textarea
                            placeholder="原因說明..."
                            value={formData.reason}
                            onChange={e => setFormData({ ...formData, reason: e.target.value })}
                        />
                    </FormControl>
                </ModalBody>
                <ModalFooter>
                    <Button onClick={onClose} mr={3}>取消</Button>
                    <Button colorScheme="brand" onClick={handleSubmit} isLoading={isLoading}>預覽並確認</Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default LeaveApplicationModal;
